// Layouts CRUD (Sprint 9, docs/09 §C1 + docs/03). A layout maps a fixed preset's
// non-main zones to playlists; the main zone stays schedule-resolved. The server
// validates playlists are org-owned and rejects a video item in a non-main zone
// (422/409). These wire types mirror the architect contract; the geometry lives
// in ../lib/layout-presets.ts (→ @seencast/shared PRESET_ZONES).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from './client.ts';
import { qk } from './keys.ts';
import type { ListEnvelope } from './client.ts';
import type { Layout, CreateLayout, UpdateLayout } from '@seencast/shared';

export type { Layout, CreateLayout, UpdateLayout, LayoutZones } from '@seencast/shared';

export function listLayouts(): Promise<Layout[]> {
  return request<ListEnvelope<Layout>>('/layouts').then((r) => r.items);
}

export function useLayouts() {
  return useQuery({ queryKey: qk.layouts, queryFn: listLayouts });
}

function invalidate(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: qk.layouts });
  qc.invalidateQueries({ queryKey: ['screens'] }); // a screen's layout label may change
}

export function useCreateLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLayout) => request<Layout>('/layouts', { method: 'POST', body }),
    onSuccess: () => invalidate(qc),
  });
}

export function useUpdateLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateLayout }) =>
      request<Layout>(`/layouts/${id}`, { method: 'PATCH', body }),
    onSuccess: () => invalidate(qc),
  });
}

export function useDeleteLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => request<void>(`/layouts/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidate(qc),
  });
}
